import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Stack, Button, Skeleton, alpha } from '@mui/material';
import { Download, Check, Close } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { differenceInCalendarDays } from 'date-fns';

import { statusLinkService } from '../services/admissions/statusLinkService';
import { StatusLinkResponse, ApplicationStatus } from '../types/admissions';
import { statusLabel } from '../components/common/StatusChip';
import PublicShell, { HeroFact } from '../components/admissions/public/PublicShell';
import DocumentChecklistInput, { documentFileProblem } from '../components/admissions/form/DocumentChecklistInput';
import dateTimeUtils from '../utils/dateTimeUtils';
import { fmtClassName } from '../utils/pdf/format';
import { useBranding, usePdfExport, fetchPhotoDataUrls } from '../utils/pdf';
import { photoFilenameOf } from '../utils/personPhoto';
import PersonPhoto from '../components/people/PersonPhoto';
import { AdmitCardDocument } from '../components/admissions/pdf';
import BusyIcon from '../components/common/BusyIcon';
import { useFeeVoucherPdf } from '../components/fees/useFeeVoucherPdf';
import { testSittingOf } from '../utils/admissionRecords';

/**
 * A family's application status at /status/:token (the application's
 * statusToken — no login). Same school-branded shell as the application
 * form. Shows where the application is and what to do next, and lets the
 * family do the things that are theirs to do: upload the documents the
 * school asked for, download the admit card and pay their challans.
 */

// Journey stages the family cares about; the 21 internal statuses map onto these.
const STAGES: Array<{ label: string; statuses: ApplicationStatus[] }> = [
  { label: 'Submitted',            statuses: ['Draft', 'Submitted', 'AppFeePending'] },
  { label: 'Under review',         statuses: ['UnderReview', 'DocsRequired', 'Ineligible'] },
  { label: 'Assessment',           statuses: ['AssessmentScheduled', 'Absent', 'Assessed'] },
  { label: 'Decision',             statuses: ['Offered', 'Waitlisted', 'Rejected', 'NotAdmitted', 'OfferDeclined', 'OfferLapsed'] },
  { label: 'Confirming admission', statuses: ['OfferAccepted', 'ConfirmedConditional', 'AdmFeePending', 'Confirmed'] },
  { label: 'Enrolled',             statuses: ['Enrolled'] },
];

const CLOSED: ApplicationStatus[] = ['OfferDeclined', 'OfferLapsed', 'Rejected', 'NotAdmitted', 'Ineligible', 'Withdrawn'];

const KIND_LABEL: Record<string, string> = {
  application_fee: 'Application fee',
  admission_fee: 'Admission fee',
  tuition: 'Tuition',
  other: 'Fee',
};

const ApplicationStatusPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<StatusLinkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  // The admit card is drawn here, in the browser, by the same renderer staff use.
  const { branding, ready: brandingReady } = useBranding(data?.school?.name, data?.school?.logo);
  const { generate: generatePdf, loading: pdfLoading } = usePdfExport();
  const vouchers = useFeeVoucherPdf(branding);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await statusLinkService.getByToken(token);
        if (!cancelled) setData(r);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'This status link is invalid.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Where the application is, and which stages it actually passed through
  // (a cycle without a test never "completes" Assessment).
  const { stageIdx, reached } = useMemo(() => {
    if (!data) return { stageIdx: 0, reached: new Set<number>() };
    const stageOf = (st: ApplicationStatus) => STAGES.findIndex(s => s.statuses.includes(st));
    const visited = new Set((data.timeline || []).map(t => stageOf(t.to)).filter(i => i >= 0));
    // Withdrawn has no stage of its own: it closes at the furthest stage reached.
    const own = stageOf(data.application.status);
    const idx = own >= 0 ? own : Math.max(0, ...Array.from(visited));
    return { stageIdx: idx, reached: visited };
  }, [data]);

  const upload = async (key: string, file: File | null) => {
    if (!file || !data) return;
    const item = (data.documents || []).find(d => d.key === key);
    const problem = item && documentFileProblem(file, item);
    if (problem) { enqueueSnackbar(problem, { variant: 'warning' }); return; }
    setUploadingKey(key);
    try {
      const documents = await statusLinkService.uploadDocument(token, key, file);
      setData(d => (d ? { ...d, documents } : d));
      enqueueSnackbar('Document received — thank you', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Upload failed. Please try again.', { variant: 'error' });
    } finally {
      setUploadingKey(null);
    }
  };

  if (loading) {
    return (
      <PublicShell width="narrow">
        <Box sx={{ p: { xs: 3, md: 5 } }}>
          <Skeleton width="50%" height={32} />
          <Skeleton variant="rectangular" height={180} sx={{ mt: 2, borderRadius: 2 }} />
        </Box>
      </PublicShell>
    );
  }
  if (error || !data) {
    return (
      <PublicShell width="narrow" eyebrow="Admissions" title="Status link not found">
        <Box sx={{ p: { xs: 3, md: 5 } }}>
          <Typography color="text.secondary">
            {error || 'This status link is invalid.'} Please contact the admissions office with your application reference.
          </Typography>
        </Box>
      </PublicShell>
    );
  }

  const app = data.application;
  const closed = CLOSED.includes(app.status);
  const className = fmtClassName(typeof app.class === 'object' ? app.class?.name : app.class);
  // The admit card matters only until the assessment is over.
  const showAdmitCard = !!data.admitCard && (app.status === 'AssessmentScheduled' || app.status === 'Absent');
  const { admitCard } = data;
  const sitting = admitCard ? testSittingOf(admitCard.cycle, admitCard.applicant) : null;
  const documents = data.documents || [];
  const photoField = data.canUploadDocuments ? data.photoField : undefined;
  const missingRequired = [
    ...(photoField?.required && !app.photoFilename ? [{ label: "Applicant's photo" }] : []),
    ...documents.filter(d => d.required && (d.status === 'missing' || d.status === 'rejected')),
  ];
  const offerDaysLeft = data.offer ? differenceInCalendarDays(new Date(data.offer.expiresAt), new Date()) : null;

  const facts: HeroFact[] = [
    { label: 'Status', value: statusLabel({ kind: 'application', value: app.status }), highlight: app.status === 'Offered' },
    { label: 'Application no.', value: app.applicationNumber },
    // Given at enrollment — the number the school knows the student by from then on.
    ...(app.grNumber ? [{ label: 'GR number', value: app.grNumber }] : []),
    ...(data.offer ? [{
      label: 'Accept the offer by',
      value: `${dateTimeUtils.formatDateForDisplay(data.offer.expiresAt)}${offerDaysLeft != null && offerDaysLeft >= 0 ? `  ·  ${offerDaysLeft === 0 ? 'today' : `${offerDaysLeft} day${offerDaysLeft === 1 ? '' : 's'} left`}` : ''}`,
      highlight: true,
    }] : []),
  ];

  const section = (title: string, children: React.ReactNode, hint?: string) => (
    <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 3.5 }, borderTop: 1, borderColor: 'divider' }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{title}</Typography>
      {hint && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{hint}</Typography>}
      <Box sx={{ mt: 2 }}>{children}</Box>
    </Box>
  );

  return (
    <PublicShell
      school={data.school}
      eyebrow="Application status"
      title={app.name}
      description={[app.cycle?.name, className].filter(Boolean).join(' · ')}
      facts={facts}
      width="narrow"
    >
      {/* ── What happens next ──────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 3.5 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
          What happens next
        </Typography>
        <Typography sx={{ mt: 0.75, fontSize: '1.05rem', lineHeight: 1.6 }}>{data.nextStep}</Typography>
        {!!data.eligibilityIssues?.length && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '12px', bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
            {data.eligibilityIssues.map(m => <Typography key={m} variant="body2">{m}</Typography>)}
          </Box>
        )}
        {missingRequired.length > 0 && data.canUploadDocuments && (
          <Typography variant="body2" sx={{ mt: 1.5, color: 'warning.dark', fontWeight: 600 }}>
            Please upload: {missingRequired.map(d => d.label).join(', ')}.
          </Typography>
        )}
      </Box>

      {/* ── Progress ───────────────────────────────────────────────── */}
      {section('Progress', (
        <Stack spacing={0}>
          {STAGES.map((stage, i) => {
            const done = (i < stageIdx && reached.has(i)) || (i === stageIdx && app.status === 'Enrolled');
            const skipped = i < stageIdx && !reached.has(i);
            const current = i === stageIdx && !done;
            const failed = current && closed;
            return (
              <Stack key={stage.label} direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.75 }}>
                <Box sx={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: (t) => (failed ? alpha(t.palette.error.main, 0.12)
                    : done ? alpha(t.palette.success.main, 0.14)
                      : current ? alpha(t.palette.primary.main, 0.14) : alpha(t.palette.text.primary, 0.06)),
                  color: failed ? 'error.main' : done ? 'success.main' : current ? 'primary.main' : 'text.disabled',
                  fontSize: 13, fontWeight: 700,
                }}>
                  {failed ? <Close sx={{ fontSize: 16 }} /> : done ? <Check sx={{ fontSize: 16 }} /> : i + 1}
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={current ? 700 : 500}
                  color={done || current ? 'text.primary' : 'text.disabled'}
                >
                  {stage.label}{skipped ? ' · not needed' : ''}
                  {current && statusLabel({ kind: 'application', value: app.status }) !== stage.label && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' · '}{statusLabel({ kind: 'application', value: app.status })}
                    </Typography>
                  )}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      ))}

      {/* ── Photo ──────────────────────────────────────────────────── */}
      {photoField?.enabled && section(
        "Applicant's photo",
        <Stack direction="row" spacing={2} alignItems="center">
          <PersonPhoto
            name={app.name}
            caption={app.applicationNumber}
            photoFilename={app.photoFilename}
            size={72}
            owner={{ statusToken: token }}
            removable={!photoField.required}
            onChanged={(photoFilename) => setData(d => (d ? {
              ...d,
              application: { ...d.application, photoFilename },
              // The admit card prints the same photo.
              admitCard: d.admitCard && typeof d.admitCard.applicant.personId === 'object'
                ? { ...d.admitCard, applicant: { ...d.admitCard.applicant, personId: { ...d.admitCard.applicant.personId, photoFilename: photoFilename ?? undefined } } }
                : d.admitCard,
            } : d))}
          />
          <Typography variant="body2" color="text.secondary">
            {app.photoFilename ? 'Tap the photo to view or change it.' : 'Tap to add a clear, recent face photo.'}
            {' '}JPG or PNG, up to 5 MB.
          </Typography>
        </Stack>,
        photoField.required ? 'Required. It goes on the admit card and the student record.' : 'It goes on the admit card and the student record.',
      )}

      {/* ── Documents ──────────────────────────────────────────────── */}
      {documents.length > 0 && data.canUploadDocuments && section(
        'Documents',
        <DocumentChecklistInput
          items={documents}
          onPick={upload}
          busyKey={uploadingKey}
        />,
        'Upload a clear photo or scan. The school verifies each one.',
      )}

      {/* ── Admit card ─────────────────────────────────────────────── */}
      {showAdmitCard && data.admitCard && section(
        'Admit card',
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem' }}>{admitCard!.applicant.admitCard?.rollNumber}</Typography>
            {sitting?.testDate && (
              <Typography variant="body2" color="text.secondary">
                {dateTimeUtils.formatDateForDisplay(sitting.testDate)}
                {sitting.testTime ? ` at ${dateTimeUtils.formatClockTime(sitting.testTime)}` : ''}
                {sitting.testVenue ? ` · ${sitting.testVenue}` : ''}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            disableElevation
            startIcon={<BusyIcon busy={pdfLoading}><Download /></BusyIcon>}
            disabled={!brandingReady || pdfLoading}
            onClick={() => generatePdf(
              async () => <AdmitCardDocument
                app={admitCard!.applicant}
                cycle={admitCard!.cycle}
                branding={branding}
                photos={await fetchPhotoDataUrls([photoFilenameOf(admitCard!.applicant)])}
                meta={{ title: 'Admit Card', subtitle: admitCard!.cycle.name, generatedAt: new Date().toISOString() }}
              />,
              `admit-card-${admitCard!.applicant.admitCard?.rollNumber}.pdf`,
            )}
          >
            {pdfLoading ? 'Preparing…' : 'Download admit card'}
          </Button>
        </Stack>,
        'Bring it, with the original documents, on the assessment day.',
      )}

      {/* ── Fees ───────────────────────────────────────────────────── */}
      {data.invoices.length > 0 && section(
        'Fees',
        <Stack spacing={1.25}>
          {data.invoices.map(inv => {
            const due = inv.outstanding > 0;
            return (
              <Stack
                key={inv._id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'center' }}
                sx={{ p: 1.75, borderRadius: '12px', border: 1, borderColor: due ? 'warning.light' : 'divider' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{KIND_LABEL[inv.kind] ?? 'Fee'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Challan {inv.invoiceNumber}
                    {due && inv.dueDate ? ` · due ${dateTimeUtils.formatDateForDisplay(inv.dueDate)}` : ''}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', color: due ? 'warning.dark' : 'success.main' }}>
                  {due ? `Rs ${inv.outstanding.toLocaleString()} due` : inv.status === 'waived' ? 'Waived' : 'Paid'}
                </Typography>
                {due && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<BusyIcon busy={vouchers.isBusy(inv._id)}><Download fontSize="small" /></BusyIcon>}
                    disabled={!brandingReady || vouchers.isBusy(inv._id)}
                    onClick={() => vouchers.download(inv._id, () => statusLinkService.getChallan(token, inv._id))}
                  >
                    Challan
                  </Button>
                )}
              </Stack>
            );
          })}
          <Typography variant="caption" color="text.secondary">Pay the challan at the bank shown on it; this page updates once the school records the payment.</Typography>
        </Stack>,
      )}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
        Keep this link safe — it’s how you follow this application.
      </Typography>
    </PublicShell>
  );
};

export default ApplicationStatusPage;
